import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import Papa from 'papaparse';
import { AlertCircle, FileText, Loader2, Settings as SettingsIcon, Upload as UploadIcon } from 'lucide-react';
import { MAX_BATCH_ROWS } from '@shipswift/shared';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/api/client';
import BatchProgressPanel from '@/components/upload/BatchProgressPanel';
import ColumnMapper from '@/components/upload/ColumnMapper';
import ValidationReview from '@/components/upload/ValidationReview';

/**
 * Bulk CSV run, as a sequence of steps.
 *
 * Purchase is deliberately the last of five, with an address review and an
 * explicit service choice in front of it — those two steps are what stop a
 * mis-mapped spreadsheet from turning into real postage.
 */
const STEPS = ['file', 'map', 'configure', 'review', 'rate', 'run'];

export default function UploadPage() {
  const [step, setStep] = useState('file');
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [mapped, setMapped] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [orderId, setOrderId] = useState(null);

  const [config, setConfig] = useState({
    orderName: '',
    orderNumber: '',
    boxId: '',
    weight: '',
  });

  const { data: boxes = [] } = useQuery({
    queryKey: ['boxes'],
    queryFn: () => api.entities.ShippingBox.list(),
  });

  const reset = () => {
    setStep('file');
    setRows([]);
    setHeaders([]);
    setMapped([]);
    setBatchId(null);
    setOrderId(null);
    setConfig({ orderName: '', orderNumber: '', boxId: '', weight: '' });
  };

  const handleFile = (file) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: 'greedy',
      complete: (results) => {
        const validHeaders = (results.meta.fields ?? []).filter((h) => h && h.trim());

        if (validHeaders.length === 0) {
          toast.error('That file has no column headers we can read.');
          return;
        }
        if (results.data.length === 0) {
          toast.error('That file has no rows.');
          return;
        }
        if (results.data.length > MAX_BATCH_ROWS) {
          toast.error(
            `That file has ${results.data.length} rows. Split it into runs of ${MAX_BATCH_ROWS} or fewer.`,
          );
          return;
        }

        setHeaders(validHeaders);
        setRows(results.data);
        setStep('map');
        toast.success(`${results.data.length} rows loaded`);
      },
      error: (error) => toast.error(`Could not read that file: ${error.message}`),
    });
  };

  const handleMapped = (mapping) => {
    const valueOf = (row, key) => (mapping[key] ? String(row[mapping[key]] ?? '').trim() : '');

    const recipients = rows.map((row) => {
      const name =
        valueOf(row, 'fullName') ||
        [valueOf(row, 'firstName'), valueOf(row, 'lastName')].filter(Boolean).join(' ');

      return {
        name,
        address1: valueOf(row, 'address1'),
        address2: valueOf(row, 'address2') || null,
        city: valueOf(row, 'city'),
        state: valueOf(row, 'state').toUpperCase(),
        zip: valueOf(row, 'zip'),
        country: (valueOf(row, 'country') || 'US').toUpperCase().slice(0, 2),
      };
    });

    // Caught here rather than by the API, so the message names the row and the
    // person can fix the spreadsheet instead of decoding a validation error.
    const bad = recipients.findIndex(
      (recipient) => !recipient.name || !recipient.address1 || !recipient.city || !recipient.zip,
    );

    if (bad !== -1) {
      toast.error(
        `Row ${bad + 1} is missing a name or address. Check the column matching, or fix the file.`,
      );
      return;
    }

    setMapped(recipients);
    setStep('configure');
  };

  const createMutation = useMutation({
    mutationFn: () => {
      const box = boxes.find((candidate) => candidate.id === config.boxId);

      return api.request('/batches', {
        method: 'POST',
        body: {
          orderName: config.orderName,
          orderNumber: config.orderNumber,
          shippingBoxId: box.id,
          box: { length: box.length, width: box.width, height: box.height },
          weight: Number.parseFloat(config.weight),
          rows: mapped,
        },
      });
    },
    onSuccess: (data) => {
      setBatchId(data.batchRunId);
      setOrderId(data.orderId);
      setStep('review');
      toast.success('Checking addresses…');
    },
    onError: (error) => toast.error(error.message ?? 'Could not start the run'),
  });

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Bulk upload</h1>
        <p className="text-slate-600">
          Upload a CSV to buy shipping labels for a whole list at once
        </p>
      </div>

      <StepIndicator current={step} />

      {step === 'file' && <FileStep boxes={boxes} onFile={handleFile} />}

      {step === 'map' && (
        <ColumnMapper
          headers={headers}
          previewRows={rows}
          onComplete={handleMapped}
          onCancel={reset}
        />
      )}

      {step === 'configure' && (
        <ConfigureStep
          config={config}
          setConfig={setConfig}
          boxes={boxes}
          rowCount={mapped.length}
          onBack={() => setStep('map')}
          onSubmit={() => createMutation.mutate()}
          submitting={createMutation.isPending}
        />
      )}

      {step === 'review' && batchId && (
        <ReviewStep batchId={batchId} onReady={() => setStep('rate')} />
      )}

      {step === 'rate' && batchId && (
        <RateStep
          batchId={batchId}
          sample={mapped[0]}
          config={config}
          boxes={boxes}
          onStarted={() => setStep('run')}
        />
      )}

      {step === 'run' && batchId && (
        <BatchProgressPanel batchId={batchId} orderId={orderId} onStartNew={reset} />
      )}
    </div>
  );
}

function StepIndicator({ current }) {
  const labels = {
    file: 'File',
    map: 'Columns',
    configure: 'Order',
    review: 'Addresses',
    rate: 'Service',
    run: 'Shipping',
  };

  const currentIndex = STEPS.indexOf(current);

  return (
    <ol className="flex flex-wrap items-center gap-2 text-sm">
      {STEPS.map((step, index) => (
        <li key={step} className="flex items-center gap-2">
          <span
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${
              index < currentIndex
                ? 'bg-green-50 border-green-200 text-green-800'
                : index === currentIndex
                  ? 'bg-blue-600 border-blue-600 text-white font-medium'
                  : 'bg-white border-slate-200 text-slate-400'
            }`}
          >
            <span className="text-xs">{index + 1}</span>
            {labels[step]}
          </span>
          {index < STEPS.length - 1 && <span className="text-slate-300">›</span>}
        </li>
      ))}
    </ol>
  );
}

function FileStep({ boxes, onFile }) {
  const [dragging, setDragging] = useState(false);

  return (
    <>
      {boxes.length === 0 && (
        <Alert className="border-yellow-200 bg-yellow-50">
          <AlertCircle className="h-4 w-4 text-yellow-600" />
          <AlertDescription className="text-yellow-800">
            Add at least one shipping box in Settings before starting a bulk run.
          </AlertDescription>
        </Alert>
      )}

      <Card className="border-none shadow-lg">
        <CardHeader className="border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardTitle className="flex items-center gap-2">
            <UploadIcon className="w-5 h-5" />
            Upload a CSV
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files[0];
              if (file) onFile(file);
            }}
            className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
              dragging ? 'border-blue-500 bg-blue-50' : 'border-slate-300 bg-slate-50'
            }`}
          >
            <FileText className="w-16 h-16 mx-auto mb-4 text-slate-400" />
            <h3 className="text-lg font-semibold text-slate-900 mb-1">Drop your CSV here</h3>
            <p className="text-slate-500 mb-4">or</p>

            <input
              type="file"
              accept=".csv,text/csv"
              id="csv-file"
              className="hidden"
              onChange={(e) => e.target.files[0] && onFile(e.target.files[0])}
            />
            <Button asChild className="bg-blue-600 hover:bg-blue-700">
              <label htmlFor="csv-file" className="cursor-pointer">
                <UploadIcon className="w-4 h-4 mr-2" />
                Choose a file
              </label>
            </Button>

            <p className="text-xs text-slate-500 mt-4">
              Any column names work — you match them up next. Up to {MAX_BATCH_ROWS} rows per run.
            </p>
          </div>
        </CardContent>
      </Card>
    </>
  );
}

function ConfigureStep({ config, setConfig, boxes, rowCount, onBack, onSubmit, submitting }) {
  const ready = config.orderName && config.orderNumber && config.boxId && config.weight;

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-green-50 to-emerald-50">
        <CardTitle className="flex items-center gap-2">
          <SettingsIcon className="w-5 h-5" />
          Order details
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        <Alert className="border-blue-200 bg-blue-50">
          <AlertDescription className="text-sm text-blue-800">
            <strong>{rowCount} shipments</strong> ready. Every parcel in this run uses the same box
            and weight.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="order-name">Order name</Label>
            <Input
              id="order-name"
              value={config.orderName}
              onChange={(e) => setConfig({ ...config, orderName: e.target.value })}
              placeholder="e.g. Holiday Mailing 2026"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="order-number">Order number</Label>
            <Input
              id="order-number"
              value={config.orderNumber}
              onChange={(e) => setConfig({ ...config, orderNumber: e.target.value })}
              placeholder="e.g. ORD-12345"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="box">Box size</Label>
            <Select
              value={config.boxId}
              onValueChange={(value) => setConfig({ ...config, boxId: value })}
            >
              <SelectTrigger id="box">
                <SelectValue placeholder="Select a box" />
              </SelectTrigger>
              <SelectContent>
                {boxes.map((box) => (
                  <SelectItem key={box.id} value={box.id}>
                    {box.name} ({box.length}&quot; × {box.width}&quot; × {box.height}&quot;)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="weight">Weight per parcel (lbs)</Label>
            <Input
              id="weight"
              type="number"
              step="0.1"
              min="0"
              value={config.weight}
              onChange={(e) => setConfig({ ...config, weight: e.target.value })}
              placeholder="e.g. 1.5"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!ready || submitting}
            className="bg-green-600 hover:bg-green-700"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating…
              </>
            ) : (
              'Check addresses'
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ReviewStep({ batchId, onReady }) {
  const { data: batch, isLoading } = useQuery({
    queryKey: ['batch', batchId],
    queryFn: () => api.request(`/batches/${batchId}`),
    // Validation runs in the background; poll until it settles.
    refetchInterval: (query) =>
      ['PENDING', 'VALIDATING'].includes(query.state.data?.status) ? 1500 : false,
  });

  if (isLoading || !batch) {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="p-12 flex items-center justify-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Loading…
        </CardContent>
      </Card>
    );
  }

  if (batch.status === 'PENDING' || batch.status === 'VALIDATING') {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="p-12 text-center space-y-3">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-blue-600" />
          <p className="font-medium text-slate-700">Checking {batch.totalItems} addresses…</p>
          <p className="text-sm text-slate-500">Nothing has been bought yet.</p>
        </CardContent>
      </Card>
    );
  }

  return <ValidationReview batch={batch} onReady={onReady} />;
}

function RateStep({ batchId, sample, config, boxes, onStarted }) {
  const [selected, setSelected] = useState(null);
  const [strategy, setStrategy] = useState('FIXED_SERVICE');
  const [ceiling, setCeiling] = useState('');

  const box = boxes.find((candidate) => candidate.id === config.boxId);

  const {
    data: rateData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['batchRates', batchId],
    queryFn: () =>
      api.request('/rates', {
        method: 'POST',
        body: {
          recipient: sample,
          parcel: {
            weight: Number.parseFloat(config.weight),
            length: box.length,
            width: box.width,
            height: box.height,
          },
        },
      }),
    retry: false,
  });

  const startMutation = useMutation({
    mutationFn: () =>
      api.request(`/batches/${batchId}/start`, {
        method: 'POST',
        body:
          strategy === 'CHEAPEST_PER_PARCEL'
            ? {
                rateStrategy: 'CHEAPEST_PER_PARCEL',
                maxAmountPerParcel: Number.parseFloat(ceiling) || undefined,
              }
            : {
                rateStrategy: 'FIXED_SERVICE',
                rateProvider: selected.provider,
                rateService: selected.serviceName,
              },
      }),
    onSuccess: onStarted,
    onError: (mutationError) => toast.error(mutationError.message ?? 'Could not start the run'),
  });

  if (isLoading) {
    return (
      <Card className="border-none shadow-lg">
        <CardContent className="p-12 flex items-center justify-center gap-3 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin" />
          Getting rates…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert className="border-red-200 bg-red-50">
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">{error.message}</AlertDescription>
      </Alert>
    );
  }

  const rates = rateData?.rates ?? [];

  return (
    <Card className="border-none shadow-lg">
      <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-indigo-50">
        <CardTitle>Choose a service</CardTitle>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        <Alert className="border-slate-200 bg-slate-50">
          <AlertDescription className="text-slate-700 text-sm">
            Priced using the first address in your file. Each parcel is priced individually when it
            ships, so the figures below are an estimate.
          </AlertDescription>
        </Alert>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setStrategy('FIXED_SERVICE')}
            className={`text-left rounded-lg border p-4 transition-colors ${
              strategy === 'FIXED_SERVICE'
                ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                : 'border-slate-200 hover:border-blue-300'
            }`}
          >
            <p className="font-semibold text-slate-900">One service for everything</p>
            <p className="text-sm text-slate-600 mt-1">
              Every parcel ships the same way. Predictable delivery times.
            </p>
          </button>

          <button
            type="button"
            onClick={() => setStrategy('CHEAPEST_PER_PARCEL')}
            className={`text-left rounded-lg border p-4 transition-colors ${
              strategy === 'CHEAPEST_PER_PARCEL'
                ? 'border-green-500 bg-green-50 ring-1 ring-green-500'
                : 'border-slate-200 hover:border-green-300'
            }`}
          >
            <p className="font-semibold text-slate-900">Cheapest for each parcel</p>
            <p className="text-sm text-slate-600 mt-1">
              Prices every address separately and takes the lowest. Usually cheaper on mixed
              destinations; delivery times will vary.
            </p>
          </button>
        </div>

        {strategy === 'CHEAPEST_PER_PARCEL' && (
          <div className="space-y-2 rounded-lg border bg-slate-50 p-4">
            <Label htmlFor="ceiling">Don&apos;t pay more than (optional)</Label>
            <div className="relative max-w-xs">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
              <Input
                id="ceiling"
                type="number"
                step="0.01"
                min="0"
                value={ceiling}
                onChange={(e) => setCeiling(e.target.value)}
                placeholder="per parcel"
                className="pl-7"
              />
            </div>
            <p className="text-xs text-slate-500">
              A parcel quoting above this stops rather than shipping, so one unexpectedly remote
              address can&apos;t go out at several times the going rate. You can retry it afterwards.
            </p>
          </div>
        )}

        <ul className={`space-y-2 ${strategy === 'CHEAPEST_PER_PARCEL' ? 'opacity-50 pointer-events-none' : ''}`}>
          {rates.map((rate) => (
            <li key={rate.rateId}>
              <button
                type="button"
                onClick={() => setSelected(rate)}
                className={`w-full text-left rounded-lg border p-4 transition-colors ${
                  selected?.rateId === rate.rateId
                    ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                    : 'border-slate-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {rate.provider.toUpperCase()} — {rate.serviceName}
                    </p>
                    {rate.estimatedDays != null && (
                      <p className="text-sm text-slate-500">
                        Est. {rate.estimatedDays} day{rate.estimatedDays === 1 ? '' : 's'}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold text-slate-900">${rate.amount.toFixed(2)}</p>
                    <p className="text-xs text-slate-500">per parcel</p>
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>

        <Button
          onClick={() => startMutation.mutate()}
          disabled={(strategy === 'FIXED_SERVICE' && !selected) || startMutation.isPending}
          className="w-full h-12 text-lg bg-blue-600 hover:bg-blue-700"
        >
          {startMutation.isPending ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Starting…
            </>
          ) : (
            `Buy labels${selected ? ` — ${selected.provider.toUpperCase()} ${selected.serviceName}` : ''}`
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
